/**
 * ビルド識別子 (版数・コミット・ビルド時刻)。
 *
 * 値は**ビルド時に外から注入する**。SPA は Vite の `VITE_INOU_*`、Worker は
 * `wrangler deploy --var` から受け取る。生成した .ts を commit させると
 * 作業ツリーが常に汚れるうえ、リリースが main へのコミットを必要とする経路が
 * 復活してしまう (版数の正はタグと GitHub Release だけ。docs/versioning.md)。
 *
 * **値が無い環境 (ローカル・テスト・E2E・preview) では dev に落ちる。**
 * 未設定でもその表示だけが控えめになり、他の経路には影響しない。
 *
 * 正規化と書式をここ 1 箇所に置くのは、画面・MCP・運営パネルで版数の
 * 見え方が割れないようにするため。
 */

export interface BuildInfo {
  /** semver。未設定なら 'dev' */
  version: string;
  /** 短縮 SHA。未設定なら 'local' */
  commit: string;
  /** ISO 8601。未設定なら null */
  buildTime: string | null;
}

export const DEV_BUILD_INFO: BuildInfo = {
  version: 'dev',
  commit: 'local',
  buildTime: null,
};

/** 空文字・空白だけ・undefined を null に均す */
function clean(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export function resolveBuildInfo(
  raw:
    | {
        version?: string | null;
        commit?: string | null;
        buildTime?: string | null;
      }
    | null
    | undefined,
): BuildInfo {
  const version = clean(raw?.version);
  return {
    // タグ (v0.9.1) がそのまま渡ってきても版数として扱う
    version: version ? version.replace(/^v/, '') : DEV_BUILD_INFO.version,
    commit: clean(raw?.commit) ?? DEV_BUILD_INFO.commit,
    buildTime: clean(raw?.buildTime),
  };
}

/** 画面に出す短い表記。`v0.9.1 (a3f21c8)` / `dev (local)` */
export function formatBuildLabel(info: BuildInfo): string {
  const version = info.version === DEV_BUILD_INFO.version ? info.version : `v${info.version}`;
  return `${version} (${info.commit})`;
}

/** 問い合わせに貼ってもらう表記。表示は短く、コピーは情報量を多く。 */
export function formatBuildDetail(info: BuildInfo): string {
  const label = formatBuildLabel(info);
  return info.buildTime ? `${label} ${info.buildTime}` : label;
}
